import type {
  BuyLevelsQualityTier,
  MoatConfidenceBand,
  ValuationZone,
} from "@features/investments/api";
import { DASHBOARD_PAGE_SIZE, DASHBOARD_PAGE_SIZE_OPTIONS } from "./constants";

export type SortKey =
  | "ticker"
  | "companyName"
  | "finalAction"
  | "valuationZone"
  | "evEbitdaTtm"
  | "pERatioTtm"
  | "evFcfTtm"
  | "overallScore"
  | "businessQualityScore"
  | "moatProxyScore"
  | "moatBand"
  | "valuationDisciplineScore"
  | "debtToFcfTtm"
  | "fcfMarginTtm"
  | "redFlagCount"
  | "warningCount"
  | "dataConfidence"
  | "buyLevelsQualityTier"
  | "refreshedAt";

export type ETFSortKey =
  | "ticker"
  | "companyName"
  | "assetClass"
  | "expenseRatio"
  | "aum"
  | "nav"
  | "avgVolume"
  | "holdingsCount"
  | "domicile";

export type DashboardScope = "equities" | "etfs";
export type EquityMode = "triage" | "screener" | "portfolio";
export type MetricPreset = "valuation" | "quality" | "risk";
export type SortDirection = "asc" | "desc";
export type ToggleFilter = "all" | "yes" | "no";
export type DashboardPageSize = (typeof DASHBOARD_PAGE_SIZE_OPTIONS)[number];

export type DashboardSort = {
  key: SortKey;
  direction: SortDirection;
};

export type DashboardETSort = {
  key: ETFSortKey;
  direction: SortDirection;
};

export type EquityFilters = {
  familyKey: string;
  finalAction: ValuationZone | "";
  valuationZone: ValuationZone | "";
  moatBand: MoatConfidenceBand | "";
  dataConfidence: "HIGH" | "MEDIUM" | "LOW" | "";
  buyLevelsQualityTier: BuyLevelsQualityTier | "";
  hasWarnings: ToggleFilter;
  hasRedFlags: ToggleFilter;
};

export type DashboardState = {
  scope: DashboardScope;
  equityMode: EquityMode;
  sort: DashboardSort;
  etfSort: DashboardETSort;
  metricPreset: MetricPreset;
  searchTerm: string;
  etfSearchTerm: string;
  filters: EquityFilters;
  selectedPortfolioFamilyKey: string;
  selectedETFFamilyKey: string;
  pageSize: DashboardPageSize;
  equityPage: number;
  portfolioPage: number;
  etfPage: number;
};

export const defaultDashboardSort: DashboardSort = {
  key: "overallScore",
  direction: "desc",
};

export const defaultDashboardETSort: DashboardETSort = {
  key: "ticker",
  direction: "asc",
};

export const defaultEquityFilters: EquityFilters = {
  familyKey: "",
  finalAction: "",
  valuationZone: "",
  moatBand: "",
  dataConfidence: "",
  buyLevelsQualityTier: "",
  hasWarnings: "all",
  hasRedFlags: "all",
};

export type DashboardAction =
  | { type: "setScope"; value: DashboardScope; }
  | { type: "setEquityMode"; value: EquityMode; }
  | { type: "setSelectedPortfolioFamily"; value: string; }
  | { type: "setSelectedETFFamily"; value: string; }
  | { type: "setMetricPreset"; value: MetricPreset; }
  | { type: "setSearchTerm"; value: string; }
  | { type: "setETFSearchTerm"; value: string; }
  | { type: "setFilter"; key: keyof EquityFilters; value: EquityFilters[keyof EquityFilters]; }
  | { type: "resetFilters"; }
  | { type: "setPageSize"; value: DashboardPageSize; }
  | { type: "setEquityPage"; value: number; }
  | { type: "setPortfolioPage"; value: number; }
  | { type: "setETFPage"; value: number; }
  | { type: "clampEquityPage"; totalPages: number; }
  | { type: "clampPortfolioPage"; totalPages: number; }
  | { type: "clampETFPage"; totalPages: number; }
  | { type: "syncPortfolioFamilies"; familyKeys: string[]; }
  | { type: "syncETFFamilies"; familyKeys: string[]; }
  | { type: "toggleSort"; key: SortKey; }
  | { type: "toggleETSort"; key: ETFSortKey; };

export function createInitialDashboardState(): DashboardState {
  return {
    scope: "equities",
    equityMode: "triage",
    sort: defaultDashboardSort,
    etfSort: defaultDashboardETSort,
    metricPreset: "valuation",
    searchTerm: "",
    etfSearchTerm: "",
    filters: defaultEquityFilters,
    selectedPortfolioFamilyKey: "",
    selectedETFFamilyKey: "",
    pageSize: DASHBOARD_PAGE_SIZE,
    equityPage: 1,
    portfolioPage: 1,
    etfPage: 1,
  };
}

function clampPage(value: number, totalPages: number): number {
  return Math.max(1, Math.min(value, Math.max(1, totalPages)));
}

function defaultSortDirection(key: SortKey): SortDirection {
  switch (key) {
    case "ticker":
    case "companyName":
    case "finalAction":
    case "valuationZone":
    case "moatBand":
    case "dataConfidence":
    case "buyLevelsQualityTier":
    case "evEbitdaTtm":
    case "pERatioTtm":
    case "evFcfTtm":
    case "debtToFcfTtm":
      return "asc";
    case "refreshedAt":
      return "desc";
    default:
      return "desc";
  }
}

function defaultETSortDirection(key: ETFSortKey): SortDirection {
  switch (key) {
    case "ticker":
    case "companyName":
    case "assetClass":
    case "domicile":
      return "asc";
    default:
      return "desc";
  }
}

function syncSelectedKey(current: string, familyKeys: string[]): string {
  if (familyKeys.length === 0) {
    return "";
  }
  if (familyKeys.includes(current)) {
    return current;
  }
  return familyKeys[0];
}

function syncOptionalSelectedKey(current: string, familyKeys: string[]): string {
  if (current === "") {
    return "";
  }
  if (familyKeys.length === 0) {
    return "";
  }
  if (familyKeys.includes(current)) {
    return current;
  }
  return "";
}

export function dashboardReducer(state: DashboardState, action: DashboardAction): DashboardState {
  switch (action.type) {
    case "setScope":
      return {
        ...state,
        scope: action.value,
      };
    case "setEquityMode":
      return {
        ...state,
        equityMode: action.value,
        metricPreset: action.value === "portfolio" ? "risk" : state.metricPreset,
        equityPage: 1,
        portfolioPage: 1,
      };
    case "setSelectedPortfolioFamily":
      return {
        ...state,
        selectedPortfolioFamilyKey: action.value,
        portfolioPage: 1,
      };
    case "setSelectedETFFamily":
      return {
        ...state,
        selectedETFFamilyKey: action.value,
        etfPage: 1,
      };
    case "setMetricPreset":
      return {
        ...state,
        metricPreset: action.value,
        equityPage: 1,
        portfolioPage: 1,
      };
    case "setSearchTerm":
      return {
        ...state,
        searchTerm: action.value,
        equityPage: 1,
        portfolioPage: 1,
      };
    case "setETFSearchTerm":
      return {
        ...state,
        etfSearchTerm: action.value,
        etfPage: 1,
      };
    case "setFilter":
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.key]: action.value,
        },
        equityPage: 1,
        portfolioPage: 1,
      };
    case "resetFilters":
      return {
        ...state,
        filters: defaultEquityFilters,
        searchTerm: "",
        equityPage: 1,
        portfolioPage: 1,
      };
    case "setPageSize":
      return {
        ...state,
        pageSize: action.value,
        equityPage: 1,
        portfolioPage: 1,
        etfPage: 1,
      };
    case "setEquityPage":
      return {
        ...state,
        equityPage: Math.max(1, action.value),
      };
    case "setPortfolioPage":
      return {
        ...state,
        portfolioPage: Math.max(1, action.value),
      };
    case "setETFPage":
      return {
        ...state,
        etfPage: Math.max(1, action.value),
      };
    case "clampEquityPage":
      return {
        ...state,
        equityPage: clampPage(state.equityPage, action.totalPages),
      };
    case "clampPortfolioPage":
      return {
        ...state,
        portfolioPage: clampPage(state.portfolioPage, action.totalPages),
      };
    case "clampETFPage":
      return {
        ...state,
        etfPage: clampPage(state.etfPage, action.totalPages),
      };
    case "syncPortfolioFamilies":
      return {
        ...state,
        selectedPortfolioFamilyKey: syncSelectedKey(state.selectedPortfolioFamilyKey, action.familyKeys),
      };
    case "syncETFFamilies":
      return {
        ...state,
        selectedETFFamilyKey: syncOptionalSelectedKey(state.selectedETFFamilyKey, action.familyKeys),
      };
    case "toggleSort":
      return {
        ...state,
        sort: state.sort.key === action.key
          ? {
            key: action.key,
            direction: state.sort.direction === "asc" ? "desc" : "asc",
          }
          : {
            key: action.key,
            direction: defaultSortDirection(action.key),
          },
        equityPage: 1,
        portfolioPage: 1,
      };
    case "toggleETSort":
      return {
        ...state,
        etfSort: state.etfSort.key === action.key
          ? {
            key: action.key,
            direction: state.etfSort.direction === "asc" ? "desc" : "asc",
          }
          : {
            key: action.key,
            direction: defaultETSortDirection(action.key),
          },
        etfPage: 1,
      };
    default:
      return state;
  }
}
